"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useMemo, useState } from "react";
import { RiDeleteBinLine } from "react-icons/ri";
import TiptapEditor from "@/components/tiptap/editor";
import Button from "@/components/ui/button";
import { DurationInput } from "@/components/ui/duration-input";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Switch from "@/components/ui/switch";
import { hydrateSmsTemplateFromSuggestions as _hydrate, smsExtraTemplateSuggestions } from "@/lib/sms-templates";
import { useSequences } from "./context";
import { describeDelay } from "./format";
import type { EffectType, SequenceDraft } from "./types";
import { useSuggestions } from "@/api/query/provider/suggestions";
void _hydrate; // re-exported by sms-templates module; silence unused import warning

/**
 * Slide-in editor for a single sequence. Renders only when `editingKey`
 * is set on the provider. Back navigation is owned by the surrounding surface
 * (e.g. `AppointmentStepDetail`) so this component renders form chrome only —
 * delete + fields. Mutations are applied immediately; if the draft is the
 * pending one, content keystrokes auto-promote it into the committed list.
 */
export function EditForm() {
  const $ = _c(95);
  const {
    state: t0,
    actions: t1,
    meta: t2
  } = useSequences();
  const {
    drafts,
    pendingDraft,
    editingKey
  } = t0;
  const {
    update,
    remove,
    setEditingKey
  } = t1;
  const {
    patientId,
    readOnly
  } = t2;
  const immediateSwitchId = useId();
  let t3;
  bb0: {
    if (!editingKey) {
      t3 = null;
      break bb0;
    }
    if (pendingDraft && pendingDraft.key === editingKey) {
      t3 = pendingDraft;
      break bb0;
    }
    let t4;
    if ($[0] !== drafts || $[1] !== editingKey) {
      t4 = drafts.find(d => d.key === editingKey) ?? null;
      $[0] = drafts;
      $[1] = editingKey;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    t3 = t4;
  }
  const draft = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["PATIENT", "TENANT", "EVENT"];
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    data: t5
  } = useSuggestions(t4, patientId);
  let t6;
  if ($[4] !== t5) {
    t6 = t5 === undefined ? [] : t5;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const apiSuggestions = t6;
  let t7;
  if ($[6] !== apiSuggestions) {
    t7 = [...apiSuggestions, ...smsExtraTemplateSuggestions];
    $[6] = apiSuggestions;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const suggestions = t7;
  let t8;
  if ($[8] !== draft?.delayMinutes) {
    t8 = () => (draft?.delayMinutes ?? 0) <= 0 ? "before" : "after";
    $[8] = draft?.delayMinutes;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const [timing, setTiming] = useState(t8);
  let t9;
  if ($[10] !== draft) {
    t9 = () => {
      if (!draft || draft.delayMinutes === 0) {
        return;
      }
      setTiming(draft.delayMinutes < 0 ? "before" : "after");
    };
    $[10] = draft;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = draft?.key;
  let t11;
  if ($[12] !== t10) {
    t11 = [t10];
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  useEffect(t9, t11);
  if (!draft || readOnly) {
    return null;
  }
  let t12;
  if ($[14] !== draft.key || $[15] !== update) {
    t12 = updates => update(draft.key, updates);
    $[14] = draft.key;
    $[15] = update;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const onChange = t12;
  const isImmediate = draft.delayMinutes === 0;
  const absDelay = Math.abs(draft.delayMinutes);
  const isBefore = timing === "before";
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Label>Effect</Label>;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== onChange) {
    t14 = v => onChange({
      effectType: v as EffectType
    });
    $[18] = onChange;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <SelectTrigger><SelectValue /></SelectTrigger>;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SelectContent><SelectItem value="SEND_SMS">Send SMS</SelectItem><SelectItem value="SEND_EMAIL">Send Email</SelectItem></SelectContent>;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== draft.effectType || $[23] !== t14) {
    t17 = <div className="space-y-1">{t13}<Select value={draft.effectType} onValueChange={t14}>{t15}{t16}</Select></div>;
    $[22] = draft.effectType;
    $[23] = t14;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Label>Timing</Label>;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== absDelay || $[27] !== onChange) {
    t19 = v_0 => {
      setTiming(v_0);
      const sign = v_0 === "before" ? -1 : 1;
      onChange({
        delayMinutes: sign * absDelay
      });
    };
    $[26] = absDelay;
    $[27] = onChange;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <SelectValue />;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== isImmediate) {
    t21 = <SelectTrigger className="w-[110px]" disabled={isImmediate}>{t20}</SelectTrigger>;
    $[30] = isImmediate;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <SelectContent><SelectItem value="before">Before</SelectItem><SelectItem value="after">After</SelectItem></SelectContent>;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== isImmediate || $[34] !== t19 || $[35] !== t21 || $[36] !== timing) {
    t23 = <Select value={timing} onValueChange={t19} disabled={isImmediate}>{t21}{t22}</Select>;
    $[33] = isImmediate;
    $[34] = t19;
    $[35] = t21;
    $[36] = timing;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== isBefore || $[39] !== onChange) {
    t24 = v_1 => {
      const sign_0 = isBefore ? -1 : 1;
      onChange({
        delayMinutes: sign_0 * (v_1 ?? 0)
      });
    };
    $[38] = isBefore;
    $[39] = onChange;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== absDelay || $[42] !== isImmediate || $[43] !== t24) {
    t25 = <DurationInput value={absDelay} onChange={t24} defaultUnit="days" placeholder="0" disabled={isImmediate} />;
    $[41] = absDelay;
    $[42] = isImmediate;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== t23 || $[46] !== t25) {
    t26 = <div className="flex items-center gap-2">{t23}{t25}</div>;
    $[45] = t23;
    $[46] = t25;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== isBefore || $[49] !== onChange) {
    t27 = checked => {
      if (checked) {
        onChange({
          delayMinutes: 0
        });
      } else {
        const sign_1 = isBefore ? -1 : 1;
        onChange({
          delayMinutes: sign_1 * 2880
        });
      }
    };
    $[48] = isBefore;
    $[49] = onChange;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== immediateSwitchId || $[52] !== isImmediate || $[53] !== t27) {
    t28 = <Switch id={immediateSwitchId} size="small" checked={isImmediate} onCheckedChange={t27} />;
    $[51] = immediateSwitchId;
    $[52] = isImmediate;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== immediateSwitchId) {
    t29 = <Label className="text-caption font-normal" htmlFor={immediateSwitchId}>Immediately</Label>;
    $[55] = immediateSwitchId;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== t28 || $[58] !== t29) {
    t30 = <div className="flex items-center gap-2">{t28}{t29}</div>;
    $[57] = t28;
    $[58] = t29;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== draft.delayMinutes) {
    t31 = describeDelay(draft.delayMinutes);
    $[60] = draft.delayMinutes;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== t31) {
    t32 = <p className="text-caption text-foreground-muted">{t31}</p>;
    $[62] = t31;
    $[63] = t32;
  } else {
    t32 = $[63];
  }
  let t33;
  if ($[64] !== t26 || $[65] !== t30 || $[66] !== t32) {
    t33 = <div className="space-y-2">{t18}{t26}{t30}{t32}</div>;
    $[64] = t26;
    $[65] = t30;
    $[66] = t32;
    $[67] = t33;
  } else {
    t33 = $[67];
  }
  let t34;
  if ($[68] !== draft.effectType || $[69] !== draft.emailSubject || $[70] !== draft.key || $[71] !== onChange) {
    t34 = draft.effectType === "SEND_EMAIL" && <div className="space-y-1"><Label htmlFor={`subject-${draft.key}`}>Subject</Label><Input id={`subject-${draft.key}`} value={draft.emailSubject ?? ""} onChange={e => onChange({
        emailSubject: e.target.value
      })} placeholder="Email subject" /></div>;
    $[68] = draft.effectType;
    $[69] = draft.emailSubject;
    $[70] = draft.key;
    $[71] = onChange;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  const t35 = draft.effectType === "SEND_EMAIL" ? "Email body" : "Message body";
  let t36;
  if ($[73] !== t35) {
    t36 = <Label>{t35}</Label>;
    $[73] = t35;
    $[74] = t36;
  } else {
    t36 = $[74];
  }
  const t37 = (draft.effectType === "SEND_EMAIL" ? draft.emailBody : draft.smsBody) ?? "";
  let t38;
  if ($[75] !== draft.effectType || $[76] !== onChange) {
    t38 = text => onChange(draft.effectType === "SEND_EMAIL" ? {
      emailBody: text
    } : {
      smsBody: text
    });
    $[75] = draft.effectType;
    $[76] = onChange;
    $[77] = t38;
  } else {
    t38 = $[77];
  }
  let t39;
  if ($[78] !== suggestions || $[79] !== t37 || $[80] !== t38) {
    t39 = <TiptapEditor content={t37} onUpdate={t38} suggestions={suggestions} placeholder="Type message... use @ to insert variables" editorClassName="min-h-[120px]" />;
    $[78] = suggestions;
    $[79] = t37;
    $[80] = t38;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  let t40;
  if ($[82] !== t36 || $[83] !== t39) {
    t40 = <div className="space-y-1">{t36}{t39}</div>;
    $[82] = t36;
    $[83] = t39;
    $[84] = t40;
  } else {
    t40 = $[84];
  }
  let t41;
  if ($[85] !== draft.key || $[86] !== remove || $[87] !== setEditingKey) {
    t41 = <Button variant="ghost" size="sm" icon={RiDeleteBinLine} onClick={() => {
      remove(draft.key);
      setEditingKey(null);
    }}>Remove Message</Button>;
    $[85] = draft.key;
    $[86] = remove;
    $[87] = setEditingKey;
    $[88] = t41;
  } else {
    t41 = $[88];
  }
  let t42;
  if ($[89] !== t17 || $[90] !== t33 || $[91] !== t34 || $[92] !== t40 || $[93] !== t41) {
    t42 = <div className="space-y-3">{t17}{t33}{t34}{t40}{t41}</div>;
    $[89] = t17;
    $[90] = t33;
    $[91] = t34;
    $[92] = t40;
    $[93] = t41;
    $[94] = t42;
  } else {
    t42 = $[94];
  }
  return t42;
}